import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowUpIcon, ArrowRightIcon, BookOpenIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
import { getModuleById, getLessonById, getNextLesson, getPreviousLesson, getModuleByCourseAndId, getLessonByCourseModuleAndId, getNextLessonInCourse, getPreviousLessonInCourse, coursesStructure } from '../data/courseData';
import { LessonSection } from '../components/LessonSection';
import { LessonNavigationController } from '../components/LessonNavigationController';
import { PointsCard } from '../components/PointsCard';
import { useProgress } from '../hooks/useProgress';
import { useAuth } from '../contexts/AuthContext';
import { LockedCourseModal } from '../components/LockedCourseModal';
import { CourseCompletionModal } from '../components/CourseCompletionModal';
import { LessonCompletionModal } from '../components/LessonCompletionModal';
import { IncompleteLessonsModal } from '../components/IncompleteLessonsModal';
import { coursesData } from '../data/coursesData';
export function Lesson() {
  const {
    moduleId,
    lessonId,
    courseId
  } = useParams<{
    moduleId: string;
    lessonId: string;
    courseId?: string;
  }>();
  const navigate = useNavigate();
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('');
  const [showLockedCourseModal, setShowLockedCourseModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showLessonCompletionModal, setShowLessonCompletionModal] = useState(false);
  const [showIncompleteLessonsModal, setShowIncompleteLessonsModal] = useState(false);
  const [incompleteLessons, setIncompleteLessons] = useState<Array<{
    moduleId: string;
    moduleName: string;
    lessonId: string;
    lessonTitle: string;
  }>>([]);
  const [nextCourseName, setNextCourseName] = useState('');
  const [completedCourseName, setCompletedCourseName] = useState('');
  const [nextCourseId, setNextCourseId] = useState('');
  const [forceUpdate, setForceUpdate] = useState(0);
  const modalShownRef = useRef(false);
  const {
    markLessonComplete,
    isLessonComplete
  } = useProgress();
  const {
    unlockedAll
  } = useAuth();
  const module = courseId && moduleId ? getModuleByCourseAndId(courseId, moduleId) : moduleId ? getModuleById(moduleId) : undefined;
  const lesson = courseId && moduleId && lessonId ? getLessonByCourseModuleAndId(courseId, moduleId, lessonId) : moduleId && lessonId ? getLessonById(moduleId, lessonId) : undefined;
  const nextLesson = courseId && moduleId && lessonId ? getNextLessonInCourse(courseId, moduleId, lessonId) : moduleId && lessonId ? getNextLesson(moduleId, lessonId) : null;
  const previousLesson = courseId && moduleId && lessonId ? getPreviousLessonInCourse(courseId, moduleId, lessonId) : moduleId && lessonId ? getPreviousLesson(moduleId, lessonId) : null;
  const isComplete = courseId && moduleId && lessonId ? isLessonComplete(courseId, moduleId, lessonId) : false;
  // Check if this is the last lesson in the current module
  const isLastLessonInModule = () => {
    if (!courseId || !moduleId || !lessonId || !module) return false;
    const lastLesson = module.lessons[module.lessons.length - 1];
    return lastLesson.id === lessonId;
  };
  // Check if this is the last lesson in the last module of the course
  const isLastLessonInCourse = () => {
    if (!courseId || !moduleId || !lessonId) return false;
    const course = coursesStructure.find(c => c.id === courseId);
    if (!course) return false;
    const lastModule = course.modules[course.modules.length - 1];
    if (lastModule.id !== moduleId) return false;
    const lastLesson = lastModule.lessons[lastModule.lessons.length - 1];
    return lastLesson.id === lessonId;
  };
  const isLastInModule = isLastLessonInModule();
  const isLastLesson = isLastLessonInCourse();
  // Reset state when lesson changes
  useEffect(() => {
    setShowBackToTop(false);
    setReadingProgress(0);
    setActiveSection('');
    window.scrollTo(0, 0);
  }, [moduleId, lessonId, courseId]);
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const progress = scrollTop / (documentHeight - windowHeight) * 100;
      setReadingProgress(progress);
      setShowBackToTop(scrollTop > 400);
      if (lesson?.sections) {
        const sections = lesson.sections;
        for (let i = sections.length - 1; i >= 0; i--) {
          const element = document.getElementById(sections[i].id);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 150) {
              setActiveSection(sections[i].id);
              break;
            }
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lesson]);
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleNextLesson = () => {
    if (courseId && moduleId && lessonId) {
      markLessonComplete(courseId, moduleId, lessonId);
      // Show lesson completion modal
      setShowLessonCompletionModal(true);
    }
  };
  const handleContinueToNextLesson = () => {
    setShowLessonCompletionModal(false);
    if (nextLesson) {
      const link = getLessonLink(nextLesson.module.id, nextLesson.lesson.id, 'courseId' in nextLesson ? nextLesson.courseId : courseId);
      // Force navigation and page reload
      navigate(link, {
        replace: false
      });
      // Force scroll to top after navigation
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };
  // Check if all lessons in the current module are completed
  const checkAllModuleLessonsCompleted = (): boolean => {
    if (!courseId || !moduleId || !module) return false;
    const incomplete: Array<{
      moduleId: string;
      moduleName: string;
      lessonId: string;
      lessonTitle: string;
    }> = [];
    // Check all lessons in the current module
    module.lessons.forEach(lesson => {
      if (!isLessonComplete(courseId, moduleId, lesson.id)) {
        incomplete.push({
          moduleId: moduleId,
          moduleName: module.title,
          lessonId: lesson.id,
          lessonTitle: lesson.title
        });
      }
    });
    if (incomplete.length > 0) {
      setIncompleteLessons(incomplete);
      return false;
    }
    return true;
  };
  // Check if all lessons in the course are completed
  const checkAllLessonsCompleted = (): boolean => {
    if (!courseId) return false;
    const course = coursesStructure.find(c => c.id === courseId);
    if (!course) return false;
    const incomplete: Array<{
      moduleId: string;
      moduleName: string;
      lessonId: string;
      lessonTitle: string;
    }> = [];
    // Check all modules and lessons
    course.modules.forEach(module => {
      module.lessons.forEach(lesson => {
        if (!isLessonComplete(courseId, module.id, lesson.id)) {
          incomplete.push({
            moduleId: module.id,
            moduleName: module.title,
            lessonId: lesson.id,
            lessonTitle: lesson.title
          });
        }
      });
    });
    if (incomplete.length > 0) {
      setIncompleteLessons(incomplete);
      return false;
    }
    return true;
  };
  // Handler for completing just the current lesson (without finishing course)
  const handleCompleteLesson = () => {
    if (courseId && moduleId && lessonId) {
      markLessonComplete(courseId, moduleId, lessonId);
      // Force re-render by updating state instead of reloading the page
      setForceUpdate(prev => prev + 1);
      // Check if this completes the entire module (but not the last lesson of the course)
      if (isLastInModule && !isLastLesson) {
        // Check if all OTHER lessons in the module are already complete
        const allOtherLessonsComplete = module?.lessons.filter(l => l.id !== lessonId).every(l => isLessonComplete(courseId, moduleId, l.id)) ?? false;
        if (allOtherLessonsComplete) {
          // All lessons in module are now complete - show module completion modal
          setTimeout(() => {
            setShowLessonCompletionModal(true);
          }, 300);
        }
      }
    }
  };
  // Handler for finishing a module (last lesson in module, but not last in course)
  const handleFinishModule = () => {
    if (courseId && moduleId && lessonId) {
      // Check if all lessons in the module are completed
      if (!checkAllModuleLessonsCompleted()) {
        // Show incomplete lessons modal
        setShowIncompleteLessonsModal(true);
        return;
      }
      // All lessons in module are complete, show completion modal
      setShowLessonCompletionModal(true);
    }
  };
  const handleFinishCourse = () => {
    if (courseId && moduleId && lessonId) {
      // Check if all lessons are completed (current lesson should already be marked complete)
      if (!checkAllLessonsCompleted()) {
        // Show incomplete lessons modal
        setShowIncompleteLessonsModal(true);
        return;
      }
      // Get current course info
      const currentCourse = coursesData.find(c => c.id === courseId);
      if (currentCourse) {
        setCompletedCourseName(currentCourse.title);
      }
      // Check if this is Training 2 (Proceduri și Tranzacții Închiriere)
      const isTraining2 = courseId === 'proceduri-inchiriere';
      if (isTraining2) {
        // For Training 2, always show completion modal with special content
        setShowCompletionModal(true);
      } else {
        // For other trainings, check for next course availability
        // Find the next course
        const currentCourseIndex = coursesData.findIndex(c => c.id === courseId);
        if (currentCourseIndex !== -1 && currentCourseIndex < coursesData.length - 1) {
          const nextCourse = coursesData[currentCourseIndex + 1];
          setNextCourseName(nextCourse.title);
          setNextCourseId(nextCourse.id);
          // Check if next course is active or if all courses are unlocked
          if (nextCourse.status === 'active' || unlockedAll) {
            // Show completion modal with option to continue
            setShowCompletionModal(true);
          } else {
            // Show locked course modal
            setShowLockedCourseModal(true);
          }
        } else {
          // This is the last course, show completion modal without next course option
          setShowCompletionModal(true);
        }
      }
    }
  };
  const handleContinueToNextCourse = () => {
    setShowCompletionModal(false);
    if (nextCourseId) {
      navigate(`/curs/${nextCourseId}`);
    } else {
      navigate('/cursuri');
    }
  };
  if (!module || !lesson) {
    return <Navigate to="/curs" replace data-id="element-8478" />;
  }
  const backLink = courseId ? `/curs/${courseId}/modul/${moduleId}` : `/modul/${moduleId}`;
  const breadcrumbCourseLink = courseId ? `/curs` : '/';
  const hasRichContent = lesson.sections && lesson.sections.length > 0;
  // Helper function to generate lesson link
  const getLessonLink = (targetModuleId: string, targetLessonId: string, targetCourseId?: string) => {
    if (targetCourseId) {
      return `/curs/${targetCourseId}/modul/${targetModuleId}/lectie/${targetLessonId}`;
    }
    return `/modul/${targetModuleId}/lectie/${targetLessonId}`;
  };
  // Determine button text and action based on position
  const getNextButtonConfig = () => {
    if (isLastLesson) {
      return {
        text: 'Finalizează training-ul',
        action: handleFinishCourse
      };
    } else if (isLastInModule) {
      return {
        text: 'Finalizează modulul',
        action: handleFinishModule
      };
    } else {
      return {
        text: 'Următoarea lecție',
        action: handleNextLesson
      };
    }
  };
  const nextButtonConfig = getNextButtonConfig();
  // Create unique key for forcing re-render when lesson changes
  const lessonKey = `${courseId}-${moduleId}-${lessonId}`;
  return <div key={lessonKey} className="w-full min-h-screen bg-[#F1F1F1]" data-id="element-8479">
      <div className="fixed top-0 left-0 right-0 h-1 bg-[#357BEB] z-50 transition-all" style={{
      width: `${readingProgress}%`
    }} data-id="element-8480" />
      <div className="bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white py-8 sm:py-12" data-id="element-8481">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-8482">
          <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-300 mb-3 sm:mb-4 overflow-x-auto" data-id="element-8483">
            <Link to={breadcrumbCourseLink} className="hover:text-white transition-colors whitespace-nowrap" data-id="element-8484">
              Training
            </Link>
            <span data-id="element-8485">/</span>
            <Link to={backLink} className="hover:text-white transition-colors whitespace-nowrap" data-id="element-8486">
              {module.title}
            </Link>
            <span data-id="element-8487">/</span>
            <span className="text-white whitespace-nowrap" data-id="element-8488">{lesson.title}</span>
          </div>
          <motion.div key={lessonKey} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-8489">
            <div className="flex items-start justify-between" data-id="element-8490">
              <div className="flex-1" data-id="element-8491">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4" data-id="element-8492">
                  {lesson.title}
                </h1>
                <p className="text-gray-300 text-base sm:text-lg mb-3 sm:mb-4" data-id="element-8493">
                  {lesson.description}
                </p>
                <div className="flex items-center space-x-4 text-xs sm:text-sm" data-id="element-8494">
                  <div className="flex items-center space-x-2" data-id="element-8495">
                    <ClockIcon className="w-4 h-4" data-id="element-8496" />
                    <span data-id="element-8497">{lesson.duration}</span>
                  </div>
                  {isComplete && <div className="flex items-center space-x-2 text-green-400" data-id="element-8498">
                      <CheckCircleIcon className="w-4 h-4" data-id="element-8499" />
                      <span data-id="element-8500">Completată</span>
                    </div>}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-id="element-8501">
        {/* Points Card */}
        <div className="mb-6" data-id="element-8502">
          <PointsCard compact data-id="element-8503" />
        </div>

        {/* Navigation Controller */}
        {courseId && moduleId && lessonId && <LessonNavigationController currentCourseId={courseId} currentModuleId={moduleId} currentLessonId={lessonId} data-id="element-8504" />}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8" data-id="element-8505">
          {hasRichContent && <aside className="hidden lg:block lg:col-span-3 order-2 lg:order-1" data-id="element-8506">
              <div className="sticky top-24 bg-white rounded-xl p-6 shadow-md" data-id="element-8507">
                <h3 className="text-sm font-bold text-[#132C53] mb-2 flex items-center space-x-2" data-id="element-8508">
                  <BookOpenIcon className="w-4 h-4" data-id="element-8509" />
                  <span data-id="element-8510">Cuprins</span>
                </h3>
                <div className="mb-4 pb-4 border-b border-gray-200" data-id="element-8511">
                  <p className="text-xs font-semibold text-[#357BEB]" data-id="element-8512">
                    {lesson.title}
                  </p>
                </div>
                <nav className="space-y-2" data-id="element-8513">
                  {lesson.sections?.map(section => <a key={section.id} href={`#${section.id}`} className={`block text-sm py-2 px-3 rounded-lg transition-all ${activeSection === section.id ? 'bg-[#357BEB] text-white font-medium' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-8514">
                      <span className="mr-2" data-id="element-8515">{section.emoji}</span>
                      {section.title}
                    </a>)}
                </nav>
              </div>
            </aside>}
          <main className={`${hasRichContent ? 'lg:col-span-9' : 'lg:col-span-12'} order-1 lg:order-2`} data-id="element-8516">
            <motion.article key={lessonKey} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white rounded-xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-md" data-id="element-8517">
              {hasRichContent ? <div data-id="element-8518">
                  {lesson.sections?.map((section, index) => <LessonSection key={section.id} section={section} index={index} data-id="element-8519" />)}
                </div> : <div data-id="element-8520">
                  <section className="mb-12" data-id="element-8521">
                    <h2 className="text-2xl font-bold text-[#132C53] mb-4" data-id="element-8522">
                      Introducere
                    </h2>
                    <div className="prose prose-lg max-w-none" data-id="element-8523">
                      <p className="text-gray-600 leading-relaxed" data-id="element-8524">
                        Conținutul acestei lecții va fi încărcat în curând.
                        Această secțiune va include informații detaliate,
                        exemple practice și exerciții pentru a te ajuta să
                        înțelegi conceptele prezentate.
                      </p>
                    </div>
                  </section>
                  <section className="mb-12" data-id="element-8525">
                    <h2 className="text-2xl font-bold text-[#132C53] mb-4" data-id="element-8526">
                      Conținut principal
                    </h2>
                    <div className="bg-blue-50 border-l-4 border-[#357BEB] p-6 rounded-r-lg mb-6" data-id="element-8527">
                      <p className="text-[#132C53] font-medium" data-id="element-8528">
                        📝 Notă: Conținutul detaliat al lecției va fi adăugat în
                        curând
                      </p>
                    </div>
                  </section>
                </div>}
              <div className="border-t border-gray-200 pt-8 mt-12" data-id="element-8529">
                <h3 className="text-xl font-bold text-[#132C53] mb-6" data-id="element-8530">
                  Navigare
                </h3>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0 gap-4" data-id="element-8531">
                  {previousLesson ? <Link to={getLessonLink(previousLesson.module.id, previousLesson.lesson.id, 'courseId' in previousLesson ? previousLesson.courseId : courseId)} className="inline-flex items-center space-x-2 text-gray-600 hover:text-[#357BEB] transition-colors" data-id="element-8532">
                      <ArrowLeftIcon className="w-4 h-4" data-id="element-8533" />
                      <span data-id="element-8534">Lecția anterioară</span>
                    </Link> : <button disabled className="inline-flex items-center space-x-2 text-gray-400 cursor-not-allowed" data-id="element-8535">
                      <ArrowLeftIcon className="w-4 h-4" data-id="element-8536" />
                      <span data-id="element-8537">Lecția anterioară</span>
                    </button>}

                  {/* Navigation buttons logic */}
                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-8538">
                    {isLastLesson ?
                  // Last lesson in course - show two-step process for course completion
                  <>
                        {!isComplete ?
                    // Step 1: Complete the lesson first
                    <button onClick={handleCompleteLesson} className="inline-flex items-center space-x-2 text-white px-6 py-3 rounded-lg font-medium transition-all shadow-lg bg-[#357BEB] hover:bg-[#2868d8]" data-id="element-8539">
                            <span data-id="element-8540">Finalizează lecția</span>
                            <CheckCircleIcon className="w-5 h-5" data-id="element-8541" />
                          </button> :
                    // Step 2: After lesson is complete, show finish course button
                    <button onClick={handleFinishCourse} className="inline-flex items-center space-x-2 text-white px-6 py-3 rounded-lg font-medium transition-all shadow-lg bg-gradient-to-r from-[#21A366] to-[#1a8553] hover:from-[#1a8553] hover:to-[#156b42]" data-id="element-8542">
                            <span data-id="element-8543">Finalizează training-ul</span>
                            <CheckCircleIcon className="w-5 h-5" data-id="element-8544" />
                          </button>}
                      </> : isLastInModule ?
                  // Last lesson in module (but not last in course) - show two-step process for module completion
                  <>
                        {!isComplete ?
                    // Step 1: Complete the lesson first
                    <button onClick={handleCompleteLesson} className="inline-flex items-center space-x-2 text-white px-6 py-3 rounded-lg font-medium transition-all shadow-lg bg-[#357BEB] hover:bg-[#2868d8]" data-id="element-8545">
                            <span data-id="element-8546">Finalizează lecția</span>
                            <CheckCircleIcon className="w-5 h-5" data-id="element-8547" />
                          </button> :
                    // Step 2: After lesson is complete, show finish module button
                    <button onClick={handleFinishModule} className="inline-flex items-center space-x-2 text-white px-6 py-3 rounded-lg font-medium transition-all shadow-lg bg-gradient-to-r from-[#357BEB] to-[#2868d8] hover:from-[#2868d8] hover:to-[#357BEB]" data-id="element-8548">
                            <span data-id="element-8549">Finalizează modulul</span>
                            <CheckCircleIcon className="w-5 h-5" data-id="element-8550" />
                          </button>}
                      </> :
                  // Regular lesson - show next lesson button
                  <>
                        {nextLesson && <button onClick={handleNextLesson} className="inline-flex items-center space-x-2 text-white px-6 py-3 rounded-lg font-medium transition-all shadow-lg bg-[#357BEB] hover:bg-[#2868d8]" data-id="element-8551">
                            <span data-id="element-8552">Următoarea lecție</span>
                            <ArrowRightIcon className="w-4 h-4" data-id="element-8553" />
                          </button>}
                      </>}
                  </div>
                </div>
              </div>
            </motion.article>
          </main>
        </div>
      </div>
      {showBackToTop && <motion.button initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.8
    }} onClick={scrollToTop} className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 bg-[#357BEB] text-white p-2 sm:p-3 rounded-full shadow-lg hover:bg-[#2868d8] transition-colors z-40" aria-label="Înapoi sus" data-id="element-8554">
          <ArrowUpIcon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-8555" />
        </motion.button>}

      {/* Incomplete Lessons Modal */}
      <IncompleteLessonsModal isOpen={showIncompleteLessonsModal} onClose={() => setShowIncompleteLessonsModal(false)} courseId={courseId || ''} courseTitle={completedCourseName} incompleteLessons={incompleteLessons} data-id="element-8556" />

      {/* Lesson Completion Modal */}
      <LessonCompletionModal isOpen={showLessonCompletionModal} onClose={() => setShowLessonCompletionModal(false)} lessonTitle={lesson?.title || ''} moduleName={module?.title || ''} onContinue={handleContinueToNextLesson} isLastInModule={isLastInModule} hasNextLesson={!!nextLesson} data-id="element-8557" />

      {/* Course Completion Modal */}
      <CourseCompletionModal isOpen={showCompletionModal} onClose={() => {
      setShowCompletionModal(false);
      navigate('/cursuri');
    }} courseTitle={completedCourseName} onContinue={handleContinueToNextCourse} hasNextCourse={!!nextCourseId} isTraining2={courseId === 'proceduri-inchiriere'} pointsEarned={100} data-id="element-8558" />

      {/* Locked Course Modal (for blocked next courses) */}
      <LockedCourseModal isOpen={showLockedCourseModal} onClose={() => {
      setShowLockedCourseModal(false);
      navigate('/cursuri');
    }} courseTitle={nextCourseName} courseId={nextCourseId} data-id="element-8559" />
    </div>;
}